/**
 * Tiny i18n table — Phase 12.4.
 *
 * Two locales for v1: `en-US` and `es-419` (Latin-American Spanish). Adding a
 * new locale is a one-table edit; the structure stays flat.
 *
 * Lookup contract:
 *   - `getLocale()` reads `localStorage['alds-locale']`, defaults 'en-US'.
 *   - `t(key, locale?)` returns the string. If a key is missing in the
 *     selected locale, it falls back to en-US; if missing there too, returns
 *     the key itself (so a screenshot says "missing.string" instead of empty).
 *   - Interpolation uses `{name}` placeholders, replaced via the second arg.
 *
 * Keep strings short. ALDS surfaces are mobile-first.
 */

export type Locale = 'en-US' | 'es-419';
export const LOCALES: readonly Locale[] = ['en-US', 'es-419'] as const;

export const LOCALE_LABELS: Record<Locale, string> = {
  'en-US': 'English (US)',
  'es-419': 'Español (LatAm)',
};

const LOCALE_KEY = 'alds-locale';

/** Read the current locale from localStorage. */
export function getLocale(): Locale {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    if (v === 'en-US' || v === 'es-419') return v;
  } catch { /* localStorage unavailable */ }
  return 'en-US';
}

/** Persist a locale choice. */
export function setLocale(locale: Locale): void {
  try { localStorage.setItem(LOCALE_KEY, locale); } catch { /* noop */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// STRINGS — keyed by stable IDs, valued per-locale.
// ─────────────────────────────────────────────────────────────────────────────

type StringTable = Record<string, string>;

const EN: StringTable = {
  // Hub / roles
  'hub.title': 'ALDS / Alternative Local Dispatch Services',
  'hub.subtitle': 'Choose your role for this session. You can switch anytime via the hub.',
  'role.caller': 'Caller',
  'role.caller.desc': 'report an incident · anonymous · per-incident key',
  'role.dispatcher': 'Dispatcher',
  'role.dispatcher.desc': 'triage incoming reports · assign responders',
  'role.responder': 'Responder',
  'role.responder.desc': 'accept dispatches · update status · evidence capture',
  'role.supervisor': 'Supervisor',
  'role.supervisor.desc': 'audit log · transparency exports · mesh health',
  'hub.meshSectionTitle': 'mesh',
  'hub.inviteNeighbor': 'invite a neighbor',
  'hub.redeemInvite': 'redeem invite code',
  'hub.sneakernetBundle': 'sneakernet bundle',

  // Caller / intake
  'intake.title': 'Report an Incident',
  'intake.subtitle': 'Anonymous — submitted under a fresh per-incident key.',
  'intake.newReport': '+ new report',
  'intake.emptyHint': 'opens the intake funnel',

  // Incident-report transient
  'report.title': 'New incident report',
  'report.subtitle': 'Anonymous. Submitted under a fresh per-incident sub-key — no link to your other reports.',
  'report.whatHappening': 'What is happening?',
  'report.severity': 'Severity (your read)',
  'report.location': 'Location (optional)',
  'report.narrative': 'Narrative (optional, no PII)',
  'report.submit': 'submit to neighborhood',
  'report.cancel': 'cancel',
  'report.submitting': 'submitting…',
  'report.submitted': 'submitted ✓',
  'report.attachProof': 'attach membership proof (✓ VERIFIED badge on the board)',

  // Categories
  'cat.medical': 'medical',
  'cat.safety': 'safety',
  'cat.utility': 'utility',
  'cat.property': 'property',
  'cat.welfare-check': 'welfare check',
  'cat.other': 'other',

  // Severities
  'sev.life-threat': 'life-threat',
  'sev.urgent': 'urgent',
  'sev.assistance': 'assistance',
  'sev.info': 'info',

  // Status
  'status.open': 'open',
  'status.triaged': 'triaged',
  'status.dispatched': 'dispatched',
  'status.on-scene': 'on-scene',
  'status.resolved': 'resolved',
  'status.cancelled': 'cancelled',

  // Common
  'common.back': 'back',
  'common.cancel': 'cancel',
  'common.close': 'close',
  'common.accept': 'accept',
  'common.decline': 'decline',
  'common.done': 'done',
  'common.useMyGps': 'use my GPS',
  'common.dropPinManually': 'drop pin manually',
  'common.locale': 'language',
};

const ES: StringTable = {
  // Hub / roles
  'hub.title': 'ALDS / Servicios Locales Alternativos de Despacho',
  'hub.subtitle': 'Elige tu rol para esta sesión. Puedes cambiarlo en cualquier momento desde el hub.',
  'role.caller': 'Llamante',
  'role.caller.desc': 'reportar un incidente · anónimo · clave por incidente',
  'role.dispatcher': 'Despachador',
  'role.dispatcher.desc': 'triaje de reportes entrantes · asigna respondedores',
  'role.responder': 'Respondedor',
  'role.responder.desc': 'acepta despachos · actualiza estado · captura evidencia',
  'role.supervisor': 'Supervisor',
  'role.supervisor.desc': 'registro de auditoría · exportaciones de transparencia · salud de la malla',
  'hub.meshSectionTitle': 'malla',
  'hub.inviteNeighbor': 'invitar a un vecino',
  'hub.redeemInvite': 'canjear código de invitación',
  'hub.sneakernetBundle': 'paquete sneakernet',

  // Caller / intake
  'intake.title': 'Reportar un Incidente',
  'intake.subtitle': 'Anónimo — enviado bajo una clave fresca por incidente.',
  'intake.newReport': '+ nuevo reporte',
  'intake.emptyHint': 'abre el embudo de intake',

  // Incident-report transient
  'report.title': 'Nuevo reporte de incidente',
  'report.subtitle': 'Anónimo. Enviado bajo una sub-clave fresca por incidente — sin vínculo a tus otros reportes.',
  'report.whatHappening': '¿Qué está pasando?',
  'report.severity': 'Severidad (tu lectura)',
  'report.location': 'Ubicación (opcional)',
  'report.narrative': 'Narrativa (opcional, sin PII)',
  'report.submit': 'enviar al vecindario',
  'report.cancel': 'cancelar',
  'report.submitting': 'enviando…',
  'report.submitted': 'enviado ✓',
  'report.attachProof': 'adjuntar prueba de membresía (insignia ✓ VERIFICADO en el tablero)',

  // Categories
  'cat.medical': 'médico',
  'cat.safety': 'seguridad',
  'cat.utility': 'utilidad',
  'cat.property': 'propiedad',
  'cat.welfare-check': 'verificación de bienestar',
  'cat.other': 'otro',

  // Severities
  'sev.life-threat': 'amenaza a la vida',
  'sev.urgent': 'urgente',
  'sev.assistance': 'asistencia',
  'sev.info': 'información',

  // Status
  'status.open': 'abierto',
  'status.triaged': 'triado',
  'status.dispatched': 'despachado',
  'status.on-scene': 'en el lugar',
  'status.resolved': 'resuelto',
  'status.cancelled': 'cancelado',

  // Common
  'common.back': 'atrás',
  'common.cancel': 'cancelar',
  'common.close': 'cerrar',
  'common.accept': 'aceptar',
  'common.decline': 'rechazar',
  'common.done': 'listo',
  'common.useMyGps': 'usar mi GPS',
  'common.dropPinManually': 'colocar pin manualmente',
  'common.locale': 'idioma',
};

const TABLES: Record<Locale, StringTable> = {
  'en-US': EN,
  'es-419': ES,
};

/**
 * Lookup a string by key. `vars` interpolates `{name}` placeholders.
 * Missing keys fall back: locale → en-US → key. Returning the key on
 * complete miss makes the missing string visible during dev.
 */
export function t(key: string, vars?: Record<string, string | number>, locale?: Locale): string {
  const loc = locale ?? getLocale();
  const table = TABLES[loc] ?? EN;
  const raw = table[key] ?? EN[key] ?? key;
  if (vars === undefined) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = vars[k];
    return v !== undefined ? String(v) : `{${k}}`;
  });
}
